import { forwardRef, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react"
import { Menu } from "@base-ui/react/menu"
import { ArrowLeft, Maximize2, Minimize2, MoreVertical, X } from "lucide-react"

// The "record view" header, ported from Attend's console: identity and actions
// in ONE band. Avatar + title + subtitle + badges + meta on the left, a quiet
// text-link action row beneath, and the chrome cluster (back / expand / ⋮ /
// close) around it. Text-link actions that don't fit fold into the ⋮ menu.

export type RecordHeaderAction = {
  id: string
  label: string
  icon?: ReactNode
  danger?: boolean
  onSelect: () => void
}

/** Toolbar actions are DATA so the header can measure them and fold the ones that don't fit into ⋮. */
export type RecordHeaderToolbarAction = {
  id: string
  label: string
  onSelect: () => void
  icon?: ComponentType<{ className?: string }>
  emphasis?: boolean
  danger?: boolean
  disabled?: boolean
  title?: string
  /** First claim on the row's width (e.g. the primary action); still folds into the ⋮ when even that does not fit. */
  keepVisible?: boolean
}

const ICON_BTN = "grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-fg/6 hover:text-fg data-[popup-open]:bg-fg/6"

export function RecordHeader({
  avatar,
  title,
  titleTrailing,
  subtitle,
  badges,
  meta,
  primary,
  toolbarActions,
  menuActions,
  onBack,
  onClose,
  onFullscreen,
  solo = false,
  flourish = true,
}: {
  /** The avatar / mark element (sized ~56px by the caller). */
  avatar?: ReactNode
  title: string
  titleTrailing?: ReactNode
  subtitle?: ReactNode
  /** Status pill(s) row under the name. */
  badges?: ReactNode
  /** Compact meta row (email · phone · company …). */
  meta?: ReactNode
  /** Primary action button, top right. */
  primary?: ReactNode
  /** Quiet text-link row beneath the identity; what does not fit folds into ⋮, pinned (`keepVisible`) actions claiming the width first. */
  toolbarActions?: RecordHeaderToolbarAction[]
  menuActions?: RecordHeaderAction[]
  onBack?: () => void
  onClose?: () => void
  onFullscreen?: () => void
  /** This pane already fills its container, so the expand toggle shows the "exit" glyph (or hides when there is no toggle). */
  solo?: boolean
  /** The accent wash and the hairline along the top; off for a plain header on the surface colour. */
  flourish?: boolean
}) {
  // Escape closes the record, unless it belongs to a field being typed in or
  // an open dialog/menu/select (focus sits inside those while they are open).
  useEffect(() => {
    if (!onClose) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (e.key !== "Escape" || e.defaultPrevented || t?.closest("input,textarea,select,[contenteditable=true],[role=dialog],[role=alertdialog],[role=menu],[role=listbox]")) return
      onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  const showFullscreen = !!onFullscreen
  const hasToolbar = !!toolbarActions && toolbarActions.length > 0
  const moreMenu =
    menuActions && menuActions.length > 0 ? (
      <Menu.Root>
        <Menu.Trigger className={ICON_BTN} title="More">
          <MoreVertical className="h-4 w-4" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner sideOffset={4} align="end" className="z-[60] outline-none">
            <Menu.Popup className="mb-pop min-w-[12rem]">
              {menuActions.map((a) => (
                <Menu.Item key={a.id} className={a.danger ? "mb-mi mb-danger" : "mb-mi"} onClick={a.onSelect}>
                  {a.icon}
                  {a.label}
                </Menu.Item>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    ) : null

  return (
    // Pinned on pointer devices so identity/actions stay put while the body
    // scrolls; in flow on touch so the whole record scrolls as one page.
    <header className={`relative z-20 shrink-0 overflow-hidden border-b border-border bg-surface [@media(hover:hover)]:sticky [@media(hover:hover)]:top-0${flourish ? " bg-gradient-to-br from-accent/8 via-accent/3 to-surface" : ""}`}>
      {flourish && <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/45 to-transparent" />}

      <div className="flex items-center gap-1.5 px-3 pt-2.5">
        {onBack && (
          <button type="button" onClick={onBack} className={ICON_BTN} title="Back">
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        {showFullscreen && (
          <button type="button" onClick={onFullscreen} className={ICON_BTN} title={solo ? "Exit full screen" : "Open full screen"}>
            <span className="relative grid h-4 w-4 place-items-center">
              <Maximize2 className={`absolute h-4 w-4 transition-all duration-300 ease-out ${solo ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"}`} />
              <Minimize2 className={`absolute h-4 w-4 transition-all duration-300 ease-out ${solo ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"}`} />
            </span>
          </button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {primary}
          {!hasToolbar && moreMenu}
          {onClose && (
            <button type="button" onClick={onClose} className={ICON_BTN} title="Close">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3.5 px-4 pt-1 pb-4">
        {avatar && <div className="shrink-0">{avatar}</div>}
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-[20px] leading-tight font-semibold tracking-tight text-fg">{title}</h1>
            {titleTrailing}
          </div>
          {subtitle && <p className="mt-0.5 truncate text-[12.5px] text-muted">{subtitle}</p>}
          {badges && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{badges}</div>}
          {meta && <div className="mt-2">{meta}</div>}
        </div>
      </div>

      {hasToolbar && <OverflowActionRow actions={toolbarActions!} menuActions={menuActions ?? []} />}
    </header>
  )
}

// Quiet text-link button for the action row. forwardRef so the hidden
// measurement layer can read each item's natural width.
const ToolbarLink = forwardRef<HTMLButtonElement, { action: RecordHeaderToolbarAction }>(function ToolbarLink({ action }, ref) {
  const { label, onSelect, icon: Icon, emphasis, disabled, danger, title } = action
  const tone = danger
    ? "text-rose-600 hover:bg-rose-50"
    : emphasis
      ? "text-accent hover:bg-fg/6"
      : "text-muted hover:bg-fg/6 hover:text-fg"
  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      disabled={disabled}
      title={title}
      className={`flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-semibold whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-40 ${tone}`}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {label}
    </button>
  )
})

// Action row that folds items which don't fit into the ⋮ menu (merged with the
// record's menuActions). A hidden, out-of-flow copy of every item gives stable
// natural widths; a ResizeObserver recomputes how many fit on resize.
const OVERFLOW_GAP = 2 // px, matches gap-0.5
const OVERFLOW_RESERVE = 46 // px reserved for the ⋮ trigger + divider

function OverflowActionRow({ actions, menuActions }: { actions: RecordHeaderToolbarAction[]; menuActions: RecordHeaderAction[] }) {
  const rowRef = useRef<HTMLDivElement>(null)
  const measureRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [visibleIds, setVisibleIds] = useState<string[]>(() => actions.map((a) => a.id))
  const pinnedKey = actions.map((a) => (a.keepVisible ? a.id : "")).join("|")

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const compute = () => {
      const avail = row.clientWidth
      const widths = measureRefs.current.map((el) => (el ? el.getBoundingClientRect().width : 0))
      const totalAll = widths.reduce((sum, w, i) => sum + w + (i ? OVERFLOW_GAP : 0), 0)
      if (totalAll <= avail && menuActions.length === 0) {
        setVisibleIds(actions.map((a) => a.id))
        return
      }
      // Pinned actions get first claim on the width, then the rest in row order;
      // nothing is ever forced onto the row, so a narrow panel keeps folding.
      const budget = avail - OVERFLOW_RESERVE
      let used = 0
      const chosen = new Set<string>()
      for (const pass of [actions.filter((a) => a.keepVisible), actions.filter((a) => !a.keepVisible)]) {
        for (const a of pass) {
          const w = widths[actions.indexOf(a)] + (chosen.size ? OVERFLOW_GAP : 0)
          if (used + w > budget) continue
          used += w
          chosen.add(a.id)
        }
      }
      setVisibleIds(actions.filter((a) => chosen.has(a.id)).map((a) => a.id))
    }
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(row)
    return () => ro.disconnect()
  }, [actions, menuActions.length, pinnedKey])

  const visible = actions.filter((a) => visibleIds.includes(a.id))
  const overflow = actions.filter((a) => !visibleIds.includes(a.id))
  const showMenu = overflow.length > 0 || menuActions.length > 0

  return (
    <div ref={rowRef} className="relative -mt-1 flex items-center gap-0.5 overflow-hidden px-2.5 pb-2.5">
      <div aria-hidden className="pointer-events-none invisible absolute top-0 left-2.5 flex items-center gap-0.5 whitespace-nowrap">
        {actions.map((a, i) => (
          <ToolbarLink key={a.id} action={a} ref={(el) => { measureRefs.current[i] = el }} />
        ))}
      </div>
      {visible.map((a) => (
        <ToolbarLink key={a.id} action={a} />
      ))}
      {showMenu && (
        <div className="ml-auto flex shrink-0 items-center pl-1">
          <span className="mr-1 h-4 w-px bg-border" />
          <Menu.Root>
            <Menu.Trigger className={ICON_BTN} title="More">
              <MoreVertical className="h-4 w-4" />
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner sideOffset={4} align="end" className="z-[60] outline-none">
                <Menu.Popup className="mb-pop min-w-[12rem]">
                  {overflow.map((a) => {
                    const Icon = a.icon
                    return (
                      <Menu.Item key={a.id} disabled={a.disabled} className={a.danger ? "mb-mi mb-danger" : "mb-mi"} onClick={a.onSelect}>
                        {Icon ? <Icon className="mb-mii" /> : null}
                        {a.label}
                      </Menu.Item>
                    )
                  })}
                  {overflow.length > 0 && menuActions.length > 0 && <div className="mx-1 my-1 h-px bg-border" />}
                  {menuActions.map((a) => (
                    <Menu.Item key={a.id} className={a.danger ? "mb-mi mb-danger" : "mb-mi"} onClick={a.onSelect}>
                      {a.icon}
                      {a.label}
                    </Menu.Item>
                  ))}
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        </div>
      )}
    </div>
  )
}
